import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Filter, Plus, MessageSquare, Clock, CheckCircle, XCircle, AlertCircle, Edit, ChevronDown, ChevronUp, History, Check, X, CalendarPlus, GitBranch, ChevronRight } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { useAuth } from '../auth';
import { apiService } from '../../services/api';
import { Visit, VisitFilters, VisitStats } from '../../types/visit';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { NewVisitModal } from './NewVisitModal';
import { EditVisitModal } from './EditVisitModal';
import { FollowUpVisitModal } from './FollowUpVisitModal';
import { VisitChainModal } from './VisitChainModal';
import { formatUTCDate, formatUTCDateTime, formatDateForBackend } from '../../utils/dateHelpers';

export const VisitTracker: React.FC = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [stats, setStats] = useState<VisitStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states - Initialize with last 30 days to 15 days forward
  const getDefaultDateRange = (): [Date, Date] => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 15);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return [startDate, endDate];
  };
  
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(getDefaultDateRange());
  const [startDate, endDate] = dateRange;
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [visitTypeFilter, setVisitTypeFilter] = useState<string>('all');
  const [showFollowUps, setShowFollowUps] = useState<boolean>(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNewVisitModal, setShowNewVisitModal] = useState(false);
  const [showEditVisitModal, setShowEditVisitModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showVisitChainModal, setShowVisitChainModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState<string>('');
  const [followUpParentVisit, setFollowUpParentVisit] = useState<Visit | null>(null);
  const [expandedVisits, setExpandedVisits] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchVisits();
    fetchStats();
    fetchUsers();
  }, [currentPage, startDate, endDate, statusFilter, visitTypeFilter, showFollowUps, selectedUsers]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchVisits = async () => {
    try {
      setLoading(true);
      const filters: VisitFilters = {
        page: currentPage,
        limit: 10
      };

      if (startDate && endDate) {
        filters.startDate = formatDateForBackend(startDate);
        filters.endDate = formatDateForBackend(endDate);
      }

      if (statusFilter && statusFilter !== 'all') filters.status = statusFilter;
      if (visitTypeFilter && visitTypeFilter !== 'all') filters.visitType = visitTypeFilter;
      if (showFollowUps) {
        filters.nextVisitRequired = true;
      }
      if (selectedUsers.length > 0) filters.userIds = selectedUsers;

      const response = await apiService.getVisits({ ...filters, groupByChain: true });
      setVisits(response.visits);
      setTotalPages(response.pagination.pages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch visits');
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitDetails = async (visitId: string) => {
    try {
      const response = await apiService.getVisit(visitId);
      return response.visit;
    } catch (err) {
      console.error('Failed to fetch visit details:', err);
      return null;
    }
  };

  const fetchStats = async () => {
    try {
      const filters = {
        ...(startDate && { startDate: formatDateForBackend(startDate) }),
        ...(endDate && { endDate: formatDateForBackend(endDate) })
      };
      const response = await apiService.getVisitStats(filters);
      setStats(response.stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getUsers();
      setAllUsers(response.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'scheduled':
        return <Clock className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      case 'no-show':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getVisitTypeColor = (type: string) => {
    switch (type) {
      case 'initial':
        return 'bg-purple-100 text-purple-800';
      case 'follow-up':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const clearFilters = () => {
    setDateRange(getDefaultDateRange());
    setStatusFilter('all');
    setVisitTypeFilter('all');
    setShowFollowUps(false);
    setSelectedUsers([]);
    setCurrentPage(1);
  };

  const toggleVisitExpanded = (visitId: string) => {
    setExpandedVisits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(visitId)) {
        newSet.delete(visitId);
      } else {
        newSet.add(visitId);
      }
      return newSet;
    });
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const getSelectedUsersLabel = () => {
    if (selectedUsers.length === 0) return "All users";
    if (selectedUsers.length === 1) {
      const user = allUsers.find(u => u.id === selectedUsers[0]);
      return user ? user.name : "1 user";
    }
    return `${selectedUsers.length} users selected`;
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2 mb-4">
          <Calendar className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Visit Tracker</h3>
        </div>
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  statusFilter === 'all' && !showFollowUps ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => {
                  setStatusFilter('all');
                  setShowFollowUps(false);
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">Total Visits:</span>
                    <span className="text-2xl font-bold">{stats.totalVisits}</span>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  statusFilter === 'completed' ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => {
                  setStatusFilter('completed');
                  setShowFollowUps(false);
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">Completed:</span>
                    <span className="text-2xl font-bold text-green-600">{stats.completedVisits}</span>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  statusFilter === 'scheduled' ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => {
                  setStatusFilter('scheduled');
                  setShowFollowUps(false);
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">Scheduled:</span>
                    <span className="text-2xl font-bold text-blue-600">{stats.scheduledVisits}</span>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  statusFilter === 'cancelled' ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => {
                  setStatusFilter('cancelled');
                  setShowFollowUps(false);
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-baseline gap-1">
                    <span className="text-sm text-muted-foreground">Cancelled:</span>
                    <span className="text-2xl font-bold text-red-600">{stats.cancelledVisits}</span>
                  </div>
                </CardContent>
              </Card>
              <Card 
                className={`cursor-pointer hover:shadow-md transition-all ${
                  showFollowUps ? 'ring-2 ring-primary shadow-md' : ''
                }`}
                onClick={() => {
                  setShowFollowUps(!showFollowUps);
                  setStatusFilter('all');
                  setCurrentPage(1);
                }}
              >
                <CardContent className="p-3">
                  {/* <div className="flex flex-col gap-1"> */}
                    {/* <div className="flex items-baseline gap-1"> */}
                      <span className="text-sm text-muted-foreground">Follow-ups Required: </span>
                      <span className="text-2xl font-bold text-purple-600">{stats.cancelledVisits}</span>
                      {/* <span className="text-2xl font-bold text-purple-600">{stats.requiresFollowUp}</span> */}
                    {/* </div> */}
                    {/* <div className="flex gap-4 text-xs">
                      <span className="text-red-600">Required: {stats.followUpUnhandled || 0}</span>
                      <span className="text-green-600">Scheduled: {stats.followUpScheduled || 0}</span>
                    </div> */}
                  {/* </div> */}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowNewVisitModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Visit
          </Button>
        </div>
      </div>

      {/* Filters - Always Visible */}
      <Card className="mb-4">
        <CardContent className="p-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">Date Range</label>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={(update) => {
                  setDateRange(update as [Date | null, Date | null]);
                }}
                placeholderText="Select date range"
                className="w-full px-2 py-1.5 text-sm border rounded-md"
                dateFormat="MMM d, yyyy"
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">Visit Type</label>
              <Select value={visitTypeFilter} onValueChange={(value) => {
                setVisitTypeFilter(value);
              }}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="initial">Initial</SelectItem>
                  <SelectItem value="follow-up">Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="relative" ref={dropdownRef}>
              <label className="text-xs font-medium mb-1 block">Users</label>
              <button
                type="button"
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="w-full h-8 px-3 text-sm text-left border rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary flex items-center justify-between"
              >
                <span className="truncate">{getSelectedUsersLabel()}</span>
                <ChevronDown className="h-4 w-4 ml-2 flex-shrink-0" />
              </button>
              
              {showUserDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="p-2">
                    <button
                      type="button"
                      onClick={() => setSelectedUsers([])}
                      className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded flex items-center justify-between"
                    >
                      <span>All users</span>
                      {selectedUsers.length === 0 && <Check className="h-4 w-4 text-primary" />}
                    </button>
                    {allUsers.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => toggleUserSelection(user.id)}
                        className="w-full text-left px-2 py-1.5 text-sm hover:bg-gray-100 rounded flex items-center justify-between"
                      >
                        <span>
                          {user.name} {!user.isActive && <span className="text-gray-500">(Inactive)</span>}
                        </span>
                        {selectedUsers.includes(user.id) && <Check className="h-4 w-4 text-primary" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={clearFilters} className="w-full h-8 text-sm">
                Clear Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Visits List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        !loading && visits.length === 0 && (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">No visits found matching your filters.</p>
            </CardContent>
          </Card>
        )
      )}
      {!loading && visits.length > 0 && (
        <div className="space-y-4">
          {visits.map((visit: any) => {
            const isExpanded = expandedVisits.has(visit.id || visit._id);
            const visitId = visit.id || visit._id;
            const isChainChild = visit.chainDepth > 0;
            
            return (
              <div key={visitId} className={isChainChild ? 'relative' : 'mb-4'}>
                {isChainChild && (
                  <div className="absolute left-4 top-0 w-8 h-8 -mt-4">
                    <div className="w-0.5 h-full bg-blue-400 mx-auto"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-400 rounded-full"></div>
                  </div>
                )}
                <Card 
                  className={`hover:shadow-md transition-shadow cursor-pointer ${
                    isChainChild ? 'ml-12 border-l-4 border-blue-400' : ''
                  }`}
                  onClick={() => toggleVisitExpanded(visitId)}
                >
                  <CardContent className="p-4">
                    {!isChainChild && visit.hasChildren && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                        <GitBranch className="h-3 w-3" />
                        Visit Series (has follow-up)
                      </div>
                    )}
                    {isChainChild && (
                      <div className="flex items-center gap-2 text-xs text-blue-600 mb-2">
                        <ChevronRight className="h-3 w-3" />
                        Follow-up visit
                      </div>
                    )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{visit.provider.name}</h3>
                        <Badge className={getStatusColor(visit.status)}>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(visit.status)}
                            {visit.status}
                          </span>
                        </Badge>
                        <Badge className={getVisitTypeColor(visit.visitType)}>
                          {visit.visitType}
                        </Badge>
                        {visit.nextVisitRequired && (
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant="outline" 
                              className={visit.followUpHandled ? "bg-green-500 text-white" : "bg-red-500 text-white"}
                            >
                              {visit.followUpHandled ? "Follow-up scheduled" : "Follow-up required"}
                              {visit.nextVisitDate && ` - ${formatUTCDate(visit.nextVisitDate)}`}
                            </Badge>
                            {visit.followUpHandled !== true && (visit.status === 'completed' || visit.status === 'cancelled') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const fullVisit = await fetchVisitDetails(visitId);
                                  if (fullVisit) {
                                    setFollowUpParentVisit(fullVisit);
                                    setShowFollowUpModal(true);
                                  }
                                }}
                                className="flex items-center gap-1"
                                title="Schedule a follow-up visit"
                              >
                                <CalendarPlus className="h-3 w-3" />
                                Schedule Follow-up
                              </Button>
                            )}
                            {visit.followUpHandled !== true && visit.status !== 'completed' && visit.status !== 'cancelled' && (
                              <span className="text-xs text-gray-500 italic">
                                Complete or cancel visit to schedule follow-up
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Date:</span> {formatUTCDate(visit.visitDate)}
                        </div>
                        <div>
                          <span className="font-medium">NPI:</span> {visit.provider.npi}
                        </div>
                        <div>
                          <span className="font-medium">Visited by:</span> {visit.visitedBy?.name || 'Unknown'}
                        </div>
                      </div>
                      {visit.purpose && (
                        <div className="mt-2 flex items-baseline gap-1">
                          <span className="font-medium text-sm">Purpose:</span>
                          <p className="text-sm text-muted-foreground truncate flex-1">{visit.purpose}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                          variant="ghost"
                          size="icon"
                          onClick={async (e) => {
                            e.stopPropagation();
                            const fullVisit = await fetchVisitDetails(visitId);
                            if (fullVisit) {
                              setSelectedVisit(fullVisit);
                              setShowEditVisitModal(true);
                            }
                          }}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MessageSquare className="h-4 w-4" />
                          <span className="text-sm">{visit.comments?.length || 0}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleVisitExpanded(visitId);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  
                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      {/* Parent/Follow-up Relationship */}
                      {(visit.parentVisitId || visit.followUpVisitId) && (
                        <div className="bg-blue-50 p-3 rounded-md">
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              {visit.parentVisitId && (
                                <div className="flex items-center gap-2 text-sm">
                                  <CalendarPlus className="h-4 w-4 text-blue-600" />
                                  <span className="font-medium">This is a follow-up visit</span>
                                </div>
                              )}
                              {visit.followUpVisitId && (
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                  <span className="font-medium">Follow-up visit has been scheduled</span>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedVisitId(visitId);
                                setShowVisitChainModal(true);
                              }}
                              className="flex items-center gap-1"
                            >
                              <GitBranch className="h-3 w-3" />
                              View Chain
                            </Button>
                          </div>
                        </div>
                      )}
                      {visit.outcome && (
                        <div className="flex items-baseline gap-1">
                          <span className="font-medium text-sm">Outcome:</span>
                          <p className="text-sm text-muted-foreground">{visit.outcome}</p>
                        </div>
                      )}
                      {visit.statusHistory && visit.statusHistory.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <History className="h-4 w-4" />
                            <span className="font-medium text-sm">Status History ({visit.statusHistory.length}):</span>
                          </div>
                          <div className="ml-6 space-y-2">
                            {visit.statusHistory.map((history: any, index: number) => (
                              <div key={index} className="flex items-start gap-3 text-sm">
                                <div className="flex-shrink-0 mt-0.5">
                                  <Badge className={getStatusColor(history.status)} variant="outline">
                                    {history.status}
                                  </Badge>
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span>by {history.changedBy?.name || 'Unknown'}</span>
                                    <span>•</span>
                                    <span>{formatUTCDateTime(history.changedAt)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {visit.comments && visit.comments.length > 0 && (
                        <div>
                          <span className="font-medium text-sm">Comments ({visit.comments.length}):</span>
                          <div className="mt-2 space-y-2">
                            {visit.comments.map((comment: any, index: number) => (
                              <Card key={comment.id || index} className="p-3 bg-muted/50">
                                <p className="text-sm">{comment.text}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <span className="text-xs text-muted-foreground">
                                    {comment.user?.name || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {comment.createdAt && formatUTCDateTime(comment.createdAt)}
                                  </span>
                                </div>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="flex items-center px-4">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      )}

      {/* New Visit Modal */}
      <NewVisitModal
        isOpen={showNewVisitModal}
        onClose={() => setShowNewVisitModal(false)}
        onSuccess={() => {
          setShowNewVisitModal(false);
          fetchVisits();
          fetchStats();
        }}
      />

      {/* Edit Visit Modal */}
      <EditVisitModal
        isOpen={showEditVisitModal}
        onClose={() => {
          setShowEditVisitModal(false);
          setSelectedVisit(null);
        }}
        onSuccess={() => {
          setShowEditVisitModal(false);
          setSelectedVisit(null);
          fetchVisits();
          fetchStats();
        }}
        visit={selectedVisit}
      />

      {/* Follow-up Visit Modal */}
      {showFollowUpModal && followUpParentVisit && (
        <FollowUpVisitModal
          isOpen={showFollowUpModal}
          onClose={() => {
            setShowFollowUpModal(false);
            setFollowUpParentVisit(null);
          }}
          onSuccess={() => {
            setShowFollowUpModal(false);
            setFollowUpParentVisit(null);
            fetchVisits();
            fetchStats();
          }}
          parentVisit={followUpParentVisit}
        />
      )}

      {/* Visit Chain Modal */}
      {showVisitChainModal && selectedVisitId && (
        <VisitChainModal
          isOpen={showVisitChainModal}
          onClose={() => {
            setShowVisitChainModal(false);
            setSelectedVisitId('');
          }}
          visitId={selectedVisitId}
        />
      )}
    </div>
  );
};
